/**
 * TaskProgressChart Component
 * Recharts AreaChart with 3 series: completed, in-progress, delayed
 * @module pages/DashboardPage/components/TaskProgressChart
 */

import { memo, useMemo, type CSSProperties } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/config/dateLocales';
import { CHART_SERIES } from '../TaskOverviewSection/TaskOverviewSection.utils';
import type { TaskProgressChartProps } from './TaskProgressChart.types';
import styles from './TaskProgressChart.module.css';

interface ChartTooltipProps {
  readonly active?: boolean;
  readonly payload?: ReadonlyArray<{
    readonly dataKey?: string;
    readonly name?: string;
    readonly color?: string;
    readonly value?: number;
  }>;
  readonly label?: string;
}

const CHART_HEIGHT = 220;

/** Custom tooltip for the chart */
function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={styles.tooltipRow}>
          <span
            className={styles.tooltipDot}
            style={{ '--tooltip-dot-color': entry.color } as CSSProperties}
          />
          <span className={styles.tooltipName}>{entry.name}</span>
          <span className={styles.tooltipValue}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Area chart displaying task progress over time with 3 stacked series */
export const TaskProgressChart = memo(function TaskProgressChart({
  data,
  height = CHART_HEIGHT,
}: TaskProgressChartProps) {
  const { t, i18n } = useTranslation();

  const formatDateTick = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(getDateLocale(i18n.language).locale, {
      day: 'numeric',
      month: 'short',
    });
    return (value: string) => fmt.format(new Date(value)).replace('.', '');
  }, [i18n.language]);

  return (
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data}>
          <defs>
            {CHART_SERIES.map((series) => (
              <linearGradient
                key={series.dataKey}
                id={`gradient-${series.dataKey}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={series.color} stopOpacity={0.25} />
                <stop
                  offset="95%"
                  stopColor={series.color}
                  stopOpacity={0.02}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--chart-grid-color)"
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <Tooltip content={<ChartTooltip />} isAnimationActive={false} />
          {CHART_SERIES.map((series) => (
            <Area
              key={series.dataKey}
              type="monotone"
              dataKey={series.dataKey}
              name={t(series.labelKey)}
              stroke={series.color}
              fill={`url(#gradient-${series.dataKey})`}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

TaskProgressChart.displayName = 'TaskProgressChart';
